import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { TenantWidgetAccessViewStore } from "../../store";
import { TenantWidgetAccessView } from "../view/TenantWidgetAccessView";
import { TenantWidgetAccessUpdateStore } from "../../store";
import { TenantWidgetAccessUpdate } from "../update/TenantWidgetAccessUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type TenantWidgetAccessModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const TenantWidgetAccessModalContent: React.FC<TenantWidgetAccessModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new TenantWidgetAccessUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new TenantWidgetAccessViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <TenantWidgetAccessView
            tenantWidgetAccessViewStore={
              storeInfo.store as TenantWidgetAccessViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <TenantWidgetAccessUpdate
            tenantWidgetAccessUpdateStore={
              storeInfo.store as TenantWidgetAccessUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
